import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, MapPin, ArrowLeft, Play } from "lucide-react";
import Seo from "@/components/Seo";
import ReviewCard from "@/components/ReviewCard";
import { VENUE_PAGES } from "@/data/venues";
import { REVIEWS, GOOGLE_URL } from "@/data/reviews";
import { SITE } from "@/data/site";
import { useLiveStats } from "@/lib/liveStats";
import { localBusinessLd, websiteLd, breadcrumbLd } from "@/lib/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Derive keyword variants from a couple's name string like "Sarah & Marty".
// Returns the original ("Sarah & Marty") and the "and" variant ("Sarah and Marty").
function coupleVariants(couple) {
  if (!couple) return [];
  return [couple, couple.replace(/\s*&\s*/g, " and ")];
}

// Case-insensitive substring match. Used both for venue keywords (a single
// strong proper noun like "Gosfield") and for full couple-name phrases.
function matchesAny(text, keywords) {
  const t = (text || "").toLowerCase();
  return keywords.some((k) => k && t.includes(k.toLowerCase()));
}

export default function VenuePage() {
  const { slug } = useParams();
  const venue = VENUE_PAGES[slug];
  const stats = useLiveStats();
  const [googleLive, setGoogleLive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/google-reviews`)
      .then((r) => setGoogleLive(r.data))
      .catch(() => setGoogleLive(null));
  }, []);

  const allReviews = useMemo(() => {
    if (!googleLive?.reviews?.length) return REVIEWS;
    const liveGoogle = googleLive.reviews
      .filter((g) => g.text)
      .map((g) => ({
        name: g.authorName || "Google reviewer",
        date: g.relativePublishTime || "Recent",
        text: g.text,
        source: "Google",
        url: g.authorUrl || GOOGLE_URL,
        iso: g.publishTime || "9999-01-01",
        rating: g.rating || 5,
      }));
    return [...liveGoogle, ...REVIEWS];
  }, [googleLive]);

  if (!venue) return <Navigate to="/" replace />;

  // Couple names derived from the film titles + any explicit venue keywords.
  const coupleKeywords = (venue.films || [])
    .flatMap((f) => coupleVariants(f.couple))
    .filter(Boolean);
  const allKeywords = [...(venue.reviewKeywords || []), ...coupleKeywords];

  const venueMatches = allReviews.filter((r) => matchesAny(r.text, allKeywords));

  // Always show 6 reviews on the page. Start with venue-specific matches,
  // top up with the best general five-star reviews so this section is never
  // half-empty or apologetic.
  const TARGET = 6;
  const fillers = allReviews
    .filter((r) => !venueMatches.includes(r))
    .slice(0, Math.max(0, TARGET - venueMatches.length));
  const displayReviews = [...venueMatches, ...fillers].slice(0, TARGET);

  const placeId = `${SITE.url}/venues/${venue.slug}#place`;
  const businessId = `${SITE.url}/#business`;

  // Build JSON-LD Review nodes for the venue-matching couples. These are the
  // strongest LLM ranking signal we can give for "best wedding videographer at
  // {venue}" type queries — each Review carries a named author, a verifiable
  // source URL, and an itemReviewed pointer to both the Place (venue) and the
  // LocalBusiness (Wedding Video Essex).
  const SOURCE_PUBLISHER = {
    Hitched: { name: "Hitched", url: "https://www.hitched.co.uk/" },
    Google: { name: "Google", url: "https://www.google.com/maps" },
    Facebook: { name: "Facebook", url: "https://www.facebook.com/" },
    Bridebook: { name: "Bridebook", url: "https://bridebook.com/" },
    "Guides for Brides": {
      name: "Guides for Brides",
      url: "https://guidesforbrides.co.uk/",
    },
  };

  const venueReviewNodes = displayReviews.map((r, i) => {
    const isVenueMatch = venueMatches.includes(r);
    return {
      "@context": "https://schema.org",
      "@type": "Review",
      "@id": `${SITE.url}/venues/${venue.slug}#review-${i}`,
      author: { "@type": "Person", name: r.name },
      datePublished: r.iso,
      reviewBody: r.text,
      url: r.url,
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating || 5,
        bestRating: 5,
        worstRating: 1,
      },
      publisher: SOURCE_PUBLISHER[r.source]
        ? { "@type": "Organization", ...SOURCE_PUBLISHER[r.source] }
        : undefined,
      // Every visible review reviews the LocalBusiness. Only reviews that
      // explicitly mention this venue also point at the Place.
      itemReviewed: isVenueMatch
        ? [
            { "@type": "Place", "@id": placeId, name: venue.name },
            { "@type": "LocalBusiness", "@id": businessId, name: "Wedding Video Essex" },
          ]
        : { "@type": "LocalBusiness", "@id": businessId, name: "Wedding Video Essex" },
    };
  });

  // Place node — attaches the matching reviews directly to the venue, with an
  // AggregateRating computed from the count of matches.
  const placeNode = {
    "@context": "https://schema.org",
    "@type": "Place",
    "@id": placeId,
    name: venue.name,
    url: venue.officialUrl,
    address: {
      "@type": "PostalAddress",
      addressLocality: venue.location.split(",")[0],
      addressRegion: "Essex",
      postalCode: venue.postcode,
      addressCountry: "GB",
    },
    ...(venueMatches.length > 0
      ? {
          review: venueReviewNodes
            .filter((n) => Array.isArray(n.itemReviewed))
            .map((n) => ({ "@id": n["@id"] })),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: 5,
            bestRating: 5,
            worstRating: 1,
            reviewCount: venueMatches.length,
          },
        }
      : {}),
  };

  const jsonLd = [
    websiteLd(),
    localBusinessLd(),
    breadcrumbLd([
      { name: "Home", url: SITE.url },
      { name: "Venues", url: `${SITE.url}/venues` },
      { name: venue.name, url: `${SITE.url}/venues/${venue.slug}` },
    ]),
    placeNode,
    ...venueReviewNodes,
    ...venue.films.map((f) => ({
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: f.title,
      description: `${f.title} filmed by Wedding Video Essex at ${venue.name}.`,
      thumbnailUrl: `https://i.ytimg.com/vi/${f.id}/maxresdefault.jpg`,
      uploadDate: f.uploadDate || "2024-01-01",
      contentUrl: `https://www.youtube.com/watch?v=${f.id}`,
      embedUrl: `https://www.youtube.com/embed/${f.id}`,
      publisher: {
        "@type": "Organization",
        name: "Wedding Video Essex",
        url: "https://www.weddingvideo.co.uk/",
      },
      ...(f.couple
        ? {
            about: { "@type": "Place", "@id": placeId, name: venue.name },
            actor: [
              { "@type": "Person", name: f.couple.split(/\s*&\s*/)[0] },
              { "@type": "Person", name: f.couple.split(/\s*&\s*/)[1] },
            ].filter((p) => p.name),
          }
        : {}),
    })),
  ];

  const [featured, ...moreFilms] = venue.films;

  return (
    <>
      <Seo
        title={`Wedding Videography & Photography at ${venue.name} | ${stats.total} Five-Star Reviews | Wedding Video Essex`}
        description={`Award-winning wedding videographers and photographers at ${venue.name} (${venue.postcode}). ${venue.films.length} real Wedding Video Essex films shot on location. ${stats.total} verified five-star reviews. ${venue.style}.`}
        canonical={`${SITE.url}/venues/${venue.slug}`}
        ogImage={`https://i.ytimg.com/vi/${featured.id}/maxresdefault.jpg`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="py-20 md:py-28 bg-stone-50 relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="venue-back-home"
            className="inline-flex items-center gap-2 font-serif italic text-stone-500 hover:text-stone-900 transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <p className="overline mb-7">Essex wedding venue · {venue.style}</p>
          <h1 className="font-serif text-[2.8rem] sm:text-[3.6rem] md:text-[5.2rem] leading-[1.02] tracking-tight text-stone-900 max-w-5xl">
            Wedding videography
            <br />
            &amp; photography at{" "}
            <em className="italic font-normal text-stone-600">{venue.name}.</em>
          </h1>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-stone-700 font-serif text-[1.05rem]">
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-4 h-4 text-stone-500" strokeWidth={1.4} />
              {venue.location} · {venue.postcode}
            </span>
            <span className="text-stone-300">·</span>
            <a
              href={venue.officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-official-link"
              className="inline-flex items-center gap-1.5 italic border-b border-stone-300 hover:border-stone-900 pb-0.5 transition-colors"
            >
              Official venue website
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <p className="mt-10 max-w-3xl font-serif text-stone-800 text-[1.18rem] md:text-[1.25rem] leading-[1.65]">
            {venue.blurb}
          </p>
        </div>
      </section>

      {/* Featured film */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10">
          <p className="overline mb-7">Filmed by Wedding Video Essex at {venue.name}</p>
          <h2 className="font-serif text-[2rem] md:text-[3rem] leading-[1.06] tracking-tight text-stone-900 max-w-3xl mb-12">
            {featured.couple
              ? `${featured.couple} — ${venue.name} wedding film.`
              : `${featured.title}.`}
          </h2>

          <div
            className="relative aspect-video overflow-hidden bg-stone-900 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)]"
            data-testid="venue-featured-film"
          >
            <iframe
              src={`https://www.youtube.com/embed/${featured.id}?rel=0`}
              title={featured.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* More films at this venue */}
      {moreFilms.length > 0 && (
        <section className="py-16 md:py-24 bg-stone-50">
          <div className="max-w-[1240px] mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
              <div>
                <p className="overline mb-5">More on location</p>
                <h2 className="font-serif text-[1.9rem] md:text-[2.6rem] leading-[1.1] tracking-tight text-stone-900">
                  More {venue.name} films,
                  <br />
                  <em className="italic font-normal text-stone-600">all shot by our team.</em>
                </h2>
              </div>
              <a
                href={`https://www.youtube.com/@WeddingVideoEssex/search?query=${encodeURIComponent(venue.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="venue-channel-link"
                className="inline-flex items-center gap-2 font-serif italic text-stone-700 hover:text-stone-900 border-b border-stone-300 hover:border-stone-700 pb-0.5 transition-colors"
              >
                See more on YouTube
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="grid md:grid-cols-2 gap-10 md:gap-14">
              {moreFilms.map((f) => (
                <article key={f.id} data-testid={`venue-film-${f.id}`}>
                  <div className="relative aspect-video overflow-hidden bg-stone-900">
                    <iframe
                      src={`https://www.youtube.com/embed/${f.id}?rel=0`}
                      title={f.title}
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                  <h3 className="mt-5 font-serif text-stone-900 text-[1.3rem] md:text-[1.5rem] leading-snug">
                    {f.couple ? `${f.couple} — ${venue.name}` : f.title}
                  </h3>
                  <p className="mt-1.5 overline text-stone-500">
                    {f.kind === "trailer"
                      ? "Wedding trailer"
                      : f.kind === "venue-promo"
                        ? "Venue promo"
                        : "Full wedding film"}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="py-20 md:py-28 bg-[#F5F0EA] grain">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10">
          <p className="overline mb-7">In couples&apos; own words</p>
          <h2 className="font-serif text-[2rem] md:text-[3rem] leading-[1.06] tracking-tight text-stone-900 max-w-4xl">
            {venueMatches.length > 0 ? (
              <>
                Reviews from couples we&apos;ve filmed at{" "}
                <em className="italic font-normal text-stone-600">{venue.name}.</em>
              </>
            ) : (
              <>
                What couples say about
                <br />
                <em className="italic font-normal text-stone-600">working with our team.</em>
              </>
            )}
          </h2>

          <div
            className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="venue-reviews-grid"
          >
            {displayReviews.map((r, i) => (
              <ReviewCard
                key={`${r.name}-${r.iso}-${i}`}
                r={r}
                testId={`venue-review-${i}`}
                sourceTestId={`venue-review-source-${i}`}
              />
            ))}
          </div>

          <div className="mt-14 md:mt-16">
            <Link
              to="/reviews"
              data-testid="venue-all-reviews-cta"
              className="btn-primary"
            >
              Read all {stats.total} reviews
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 md:py-32 bg-stone-900 text-stone-50">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <p className="overline text-stone-500 mb-7">Filming at {venue.name}?</p>
          <h2 className="font-serif text-[2.2rem] md:text-[3.4rem] leading-[1.06] tracking-tight">
            Speak to the person who&apos;ll
            <br />
            <em className="italic font-normal text-stone-400">
              actually film your day at {venue.name}.
            </em>
          </h2>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-cta-book"
              className="btn-primary"
            >
              <Play className="w-3.5 h-3.5" />
              Check my date
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <Link
              to="/reviews"
              data-testid="venue-cta-reviews"
              className="inline-flex items-center gap-2 font-serif italic text-stone-300 hover:text-stone-50 border-b border-stone-700 hover:border-stone-200 pb-0.5"
            >
              Read all {stats.total} reviews
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
